import PropTypes from "prop-types";
import React, { createRef, useEffect, useRef, useState } from "react";

import { classNames } from "@swa-ui/string";

import { Button, buttonPropTypes } from "../Button";
import { keyCodes } from "../defines/keyCodes";
import {
  HorizontalGestureScroller,
  horizontalGestureScrollerPropTypes,
} from "../HorizontalGestureScroller";
import styles from "./ButtonGroup.module.scss";

/**
 * ButtonGroup presents a horizontal list of buttons where only one can be selected at a time. When
 * all given buttons cannot be shown, the buttons can be scrolled with a mouse wheel or touchscreen
 * gesture.
 *
 * See ButtonGroup.technotes.mdx for implementation discussion.
 */

export const ButtonGroup = (props) => {
  const {
    alignment,
    animate,
    buttonList,
    className,
    defaultSelectedIndex,
    descriptionLeft,
    descriptionRight,
    enableKeyboardSupport,
    equalSize,
    focusOnSelectionChange,
    moreContentIndicator,
    onClick,
    renderToken,
    role,
    selectedIndex,
    showSelected,
    size,
    spaceBetween,
    styleType,
  } = props;
  const [focusedButtonIndex, setFocusedButtonIndex] = useState(-1);
  const [selectedButtonIndex, setSelectedButtonIndex] = useState(
    selectedIndex || defaultSelectedIndex
  );
  const [scrollPosition, setScrollPosition] = useState(0);
  const buttonRefs = useRef([]);
  const container = useRef();

  useEffect(() => {
    setScrollPosition(ensureButtonIsFullyVisible());
  }, [selectedButtonIndex, focusedButtonIndex]);

  useEffect(() => {
    buttonRefs.current = buttonList.map((_, index) => buttonRefs.current[index] || createRef());
  }, [buttonList]);

  useEffect(() => {
    if (selectedIndex && selectedIndex !== -1 && buttonRefs.current[selectedIndex]) {
      setSelectedButtonIndex(selectedIndex);
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (selectedIndex !== undefined) {
      setSelectedButtonIndex(selectedIndex);

      if (focusOnSelectionChange && buttonRefs?.current?.[selectedIndex]?.current) {
        buttonRefs.current[selectedIndex].current.focus();
      }
    } else {
      setSelectedButtonIndex(defaultSelectedIndex);
    }
  }, [selectedIndex, renderToken]);

  return buttonList ? (
    <div {...getProps()}>
      {descriptionLeft}
      {!equalSize ? (
        <HorizontalGestureScroller {...getScrollerProps()}>
          {buttonList.map(renderButton)}
        </HorizontalGestureScroller>
      ) : (
        <div className={styles.equalSize} ref={container} role={role}>
          {buttonList.map(renderButton)}
        </div>
      )}
      {descriptionRight}
    </div>
  ) : null;

  function renderButton(buttonInfo, index) {
    return <Button {...getButtonProps(buttonInfo, index)}>{buttonInfo.label}</Button>;
  }

  function getProps() {
    return {
      className: classNames(className, styles.buttonGroup, {
        [styles.fullWidth]: equalSize,
      }),
      onKeyDown: enableKeyboardSupport ? handleKeyDown : undefined,
    };
  }

  function getButtonProps(buttonInfo, index) {
    const buttonInfoProps = buttonInfo.props ?? {};
    const isButtonFocused = selectedButtonIndex === index;

    return {
      ...buttonInfoProps,
      animate: moreContentIndicator ? false : animate,
      "aria-expanded": enableKeyboardSupport && isButtonFocused,
      className: getButtonClass(buttonInfoProps, index),
      key: buttonInfoProps.key ?? index,
      onClick: () => handleClick(index),
      onFocus: () => (enableKeyboardSupport ? undefined : handleFocus(index)),
      ref: buttonRefs.current[index],
      selected: showSelected && index === selectedButtonIndex,
      size,
      styleType: buttonInfoProps.styleType || styleType,
      tabIndex: enableKeyboardSupport ? (isButtonFocused ? 0 : -1) : 0,
    };
  }

  function getScrollerProps() {
    const isButtonStyleTypeTab = buttonList.some(
      (buttonInfoProps) => buttonInfoProps.styleType === "tab"
    );

    return {
      alignment,
      "aria-label-next": props["aria-label-next"],
      "aria-label-previous": props["aria-label-previous"],
      fadeEdge: !equalSize,
      moreContentIndicator,
      ref: container,
      role: isButtonStyleTypeTab || styleType === "tab" ? "tablist" : role,
      scrollPosition,
      tabIndex: -1,
    };
  }

  function getButtonClass(buttonInfoProps, index) {
    const lastButton = index === buttonList.length - 1;

    return classNames(buttonInfoProps.className, {
      [styles.option]: equalSize,
      [styles.spaceLarge]: spaceBetween === "large" && !lastButton,
      [styles.spaceSmall]: spaceBetween === "small" && !lastButton,
    });
  }

  function handleKeyDown(event) {
    const { key } = event;
    const buttonCount = buttonList.length;
    let newIndex;

    if (key === keyCodes.KEY_END) {
      newIndex = buttonCount - 1;
    } else if (key === keyCodes.KEY_HOME) {
      newIndex = 0;
    } else if (key === keyCodes.KEY_LEFT) {
      newIndex = (selectedButtonIndex + buttonCount - 1) % buttonCount;
    } else if (key === keyCodes.KEY_RIGHT) {
      newIndex = (selectedButtonIndex + 1) % buttonCount;
    }

    if (newIndex !== undefined) {
      event.preventDefault();
      event.stopPropagation();
      setFocusedButtonIndex(newIndex);
      updateButtonState(newIndex);

      // Programmatically focus the button for keyboard support
      if (buttonRefs.current[newIndex]?.current) {
        buttonRefs.current[newIndex].current.focus();
      }
    }
  }

  function handleClick(index) {
    updateButtonState(index);
  }

  function handleFocus(index) {
    setFocusedButtonIndex(index);
  }

  function updateButtonState(index) {
    const buttonInfoProps = buttonList[index].props ?? {};
    const { onClick: buttonOnClick } = buttonInfoProps;

    setSelectedButtonIndex(index);
    buttonOnClick?.();
    onClick?.(index);
  }

  function ensureButtonIsFullyVisible() {
    const index = focusedButtonIndex !== -1 ? focusedButtonIndex : selectedButtonIndex;
    let targetScrollLeft = 0;

    if (index !== -1 && buttonRefs.current[index] && container.current) {
      const button = buttonRefs.current[index].current;
      const containerElement = container.current;
      const buttonLeft = button.offsetLeft;
      const buttonRight = buttonLeft + button.offsetWidth;
      const containerWidth = containerElement.offsetWidth;
      const currentScrollLeft = Math.abs(scrollPosition);

      targetScrollLeft = currentScrollLeft;

      if (buttonLeft < currentScrollLeft) {
        targetScrollLeft = Math.max(0, buttonLeft);
      } else if (buttonRight > currentScrollLeft + containerWidth) {
        targetScrollLeft = buttonRight - containerWidth;
      }
    }

    return -targetScrollLeft;
  }
};

ButtonGroup.displayName = "ButtonGroup";

export const buttonGroupPropTypes = {
  /** Buttons will be left aligned by default but can be centered when set to 'center'. */
  alignment: horizontalGestureScrollerPropTypes.alignment,

  /** Button content changes will animate, using TransitionBlock, unless turned off. */
  animate: PropTypes.bool,

  /** aria-label text to provide accessibility description for HorizontalGestureScroller's next page button. */
  "aria-label-next": PropTypes.string.isRequired,

  /** aria-label text to provide accessibility description for HorizontalGestureScroller's previous page button. */
  "aria-label-previous": PropTypes.string.isRequired,

  /** Buttons to display. If you are going to implement a dynamic button list, you will need to pass a key prop to prevent rendering issues */
  buttonList: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
      props: PropTypes.shape({ ...buttonPropTypes, key: PropTypes.string }),
    })
  ),

  /**
   * Additional classes for positioning the component. Given classes may only position this component
   * for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Initial index of selected button for "uncontrolled" components. */
  defaultSelectedIndex: PropTypes.number,

  /** Option JSX content to be displayed to the left of the button group. */
  descriptionLeft: PropTypes.node,

  /** Option JSX content to be displayed to the right of the button group. */
  descriptionRight: PropTypes.node,

  /**
   * Enables keyboard navigation. With this enabled, arrows/page keys will move highlight between
   * buttons. If not enabled, tab will allow focus to move between buttons.
   */
  enableKeyboardSupport: PropTypes.bool,

  /** Setting this prop to true will set all buttons to the same width. */
  equalSize: PropTypes.bool,

  /** When set, ButtonGroup will always set focus on a newly selected item. */
  focusOnSelectionChange: PropTypes.bool,

  /** Denotes if a "faded edge" will be rendered to indicate more content is scrollable. */
  moreContentIndicator: PropTypes.bool,

  /**
   * Callback function to learn when a button is clicked. The only parameter returned is the index
   * of the selected button.
   */
  onClick: PropTypes.func,

  /**
   * renderToken provides a way to force a render. Typically ButtonGroup will re-render when
   * selectedIndex changes, but if renderToken changes since the last render, ButtonGroup will
   * re-render and set focus to the selected item (provided that focusOnSelectionChange is set)
   * even if selectedIndex doesn't change.
   */
  renderToken: PropTypes.string,

  /** The role attribute for the button container. */
  role: PropTypes.string,

  /**
   * Index of selected button for "controlled" components. If set to negative one, no selected state
   * will be shown.
   */
  selectedIndex: PropTypes.number,

  /**
   * The currently selected button can show, or not show, its selected state. When ButtonGroup is
   * used to represent a radio button group, the selected button is typically denoted, but when
   * ButtonGroup is used as a list of buttons/links, showing the current selection may not desired
   * and can be turned off.
   */
  showSelected: PropTypes.bool,

  /**
   * Button size can be set to change the size and width of each button. See button component for
   * more info.
   */
  size: buttonPropTypes.size,

  /** Distance between buttons. */
  spaceBetween: PropTypes.oneOf(["large", "none", "small"]),

  /** Button style to be used for all buttons in the buttonList. */
  styleType: buttonPropTypes.styleType,
};

ButtonGroup.propTypes = buttonGroupPropTypes;

ButtonGroup.defaultProps = {
  defaultSelectedIndex: 0,
  equalSize: false,
  focusOnSelectionChange: true,
  moreContentIndicator: true,
  showSelected: true,
  size: "large",
  spaceBetween: "none",
  styleType: "capsule",
};
